import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { useKeystone, useSelectedPolicy } from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import { type WindDefenseContextState } from "pages/offer/wind-defense/wind-defense-flow";
import { useFlags } from "utils/feature-flags";

export type WindDefenseEnrollmentValues = {
  offeringId?: string;
  offerId?: string;
  insightPolicyId?: string;
  additionalDetails?: {
    firstName?: string;
    lastName?: string;
    emailAddress?: string;
    phone?: string;
  };
};

export const useWindDefenseEnrollmentSteps = (
  state: WindDefenseContextState,
) => {
  const { windDefenseReduceFlow } = useFlags();
  const { pathname } = useLocation();
  const skipPropertyStep = state?.eligibleWindDefensePolicies?.length === 1;

  return useMemo(() => {
    const route = pathname.split("/").pop();
    const totalSteps = windDefenseReduceFlow ? (skipPropertyStep ? 2 : 3) : 5;
    const stepLabels = windDefenseReduceFlow
      ? skipPropertyStep
        ? ["Confirm details", "Review and finish"]
        : ["Select properties", "Confirm details", "Review and finish"]
      : [];

    let step = 1;
    switch (route) {
      case "contact-information":
        step = windDefenseReduceFlow ? (skipPropertyStep ? 1 : 2) : 2;
        break;
      case "contact-information-confirmation":
        step = windDefenseReduceFlow ? (skipPropertyStep ? 2 : 3) : 5;
        break;
    }

    return { stepLabels, totalSteps, step };
  }, [pathname, windDefenseReduceFlow, skipPropertyStep]);
};

export const useWindDefenseEnrollment = (state: WindDefenseContextState) => {
  const keystone = useKeystone();
  const { data } = useSelectedPolicy();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const { firstName, lastName, emailAddress, phone, offeringId, offerId } =
    state ?? {};

  const enrollWindDefense = useCallback(async () => {
    setLoading(true);
    setError(false);
    const windDefenseEnrollmentBody: WindDefenseEnrollmentValues = {
      offeringId,
      offerId,
      insightPolicyId: data?.keystonePolicyId,
      additionalDetails: {
        firstName,
        lastName,
        emailAddress,
        phone,
      },
    };

    const response = await keystone.enrollWindDefense(
      windDefenseEnrollmentBody,
    );
    if (isError(response)) {
      setError(true);
    }
    setLoading(false);

    return !isError(response);
  }, [
    emailAddress,
    firstName,
    keystone,
    lastName,
    offerId,
    offeringId,
    phone,
    data?.keystonePolicyId,
  ]);

  return { enrollWindDefense, loading, error };
};

export const useMultiWindDefenseEnrollment = (
  state: WindDefenseContextState,
) => {
  const keystone = useKeystone();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const { firstName, lastName, emailAddress, phone } = state ?? {};

  const enrollWindDefense = useCallback(
    async ({ eligibilityInfo }) => {
      setLoading(true);
      setError(false);
      const windDefenseEnrollmentBody: WindDefenseEnrollmentValues = {
        offeringId: eligibilityInfo?.offeringId,
        offerId: eligibilityInfo?.offerId,
        insightPolicyId: eligibilityInfo?.insightPolicyId,
        additionalDetails: {
          firstName,
          lastName,
          emailAddress,
          phone,
        },
      };

      const response = await keystone.enrollWindDefense(
        windDefenseEnrollmentBody,
      );
      if (isError(response)) {
        setError(true);
      }
      setLoading(false);

      return !isError(response);
    },
    [emailAddress, firstName, keystone, lastName, phone],
  );

  return { enrollWindDefense, loading, error };
};
